<template>
  <nav v-if="show" class="km-tabbar" :class="{ 'km-tabbar--scrolled': scrolled }">
    <div class="km-tabbar__inner">
      <!-- All 7 items as equal flex siblings — 3 left, FAB in slot 4, 3 right -->
      <router-link
        v-for="t in tabLabels"
        :key="t.path"
        :to="t.path"
        class="km-tab"
        :class="[{ active: isActive(t) }, 'km-tab--'+t.id]"
      >
        <span class="km-tab__icon">
          <!-- Discover (center FAB) -->
          <svg v-if="t.id === 'discover'" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient :id="'kmg-disc-'+_uid" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#ffd3e4"/><stop offset="1" stop-color="#fff"/>
              </linearGradient>
            </defs>
            <path d="M16 5.5 18.4 11 24 11.4 19.6 15.1 21 21 16 18 11 21 12.4 15.1 8 11.4 13.6 11 16 5.5Z"
                  :fill="'url(#kmg-disc-'+_uid+')'" stroke="#fff" stroke-width="1.6" stroke-linejoin="round"/>
          </svg>
          <!-- Home -->
          <svg v-else-if="t.id === 'home'" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 12.3 14 4l10 8.3V23a2 2 0 0 1-2 2h-4.5v-6a2 2 0 0 0-2-2h-3a2 2 0 0 0-2 2v6H6a2 2 0 0 1-2-2V12.3Z"
                  stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"
                  :fill="isActive(t) ? 'url(#kmg-home)' : 'none'"/>
          </svg>
          <!-- Video/Cam -->
          <svg v-else-if="t.id === 'video'" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="5" width="22" height="18" rx="4" stroke="currentColor" stroke-width="2.2"
                  :fill="isActive(t) ? 'url(#kmg-video)' : 'none'"/>
            <path d="M12 10.5v7l6-3.5-6-3.5Z" :fill="isActive(t) ? '#fff' : 'currentColor'" stroke="none"/>
          </svg>
          <!-- Chats -->
          <svg v-else-if="t.id === 'chats'" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 7a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3h-6.5l-4.2 3.2a1 1 0 0 1-1.6-.8V19H8a3 3 0 0 1-3-3V7Z"
                  stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"
                  :fill="isActive(t) ? 'url(#kmg-chats)' : 'none'"/>
            <circle cx="11" cy="11.5" r="1.4" :fill="isActive(t) ? '#fff' : 'currentColor'"/>
            <circle cx="15" cy="11.5" r="1.4" :fill="isActive(t) ? '#fff' : 'currentColor'"/>
            <circle cx="19" cy="11.5" r="1.4" :fill="isActive(t) ? '#fff' : 'currentColor'"/>
          </svg>
          <!-- Matches / Choose -->
          <svg v-else-if="t.id === 'choose'" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 24.5s-9-5.4-11-11C1.2 9.4 3.9 5.5 7.7 5.5c2.2 0 4.1 1.2 5.3 3a6.1 6.1 0 0 1 5.5-3c3.8 0 6 3.9 4.3 8-2.3 5.7-8.8 11-8.8 11Z"
                  stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"
                  :fill="isActive(t) ? 'url(#kmg-choose)' : 'none'"/>
          </svg>
          <!-- Lottery -->
          <svg v-else-if="t.id === 'lottery'" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 3 16.6 10.4 24.4 10.8 18.3 15.6 20.5 23.2 14 18.9 7.5 23.2 9.7 15.6 3.6 10.8 11.4 10.4 14 3Z"
                  stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"
                  :fill="isActive(t) ? 'url(#kmg-spin)' : 'none'"/>
          </svg>
          <!-- Mine / Profile -->
          <svg v-else viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="9.5" r="4.5" stroke="currentColor" stroke-width="2.2"
                    :fill="isActive(t) ? 'url(#kmg-mine)' : 'none'"/>
            <path d="M4.5 24.5c1.2-4.6 5-7 9.5-7s8.3 2.4 9.5 7"
                  stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                  :fill="isActive(t) ? 'url(#kmg-mine)' : 'none'" fill-opacity=".15"/>
          </svg>
        </span>
        <span class="km-tab__label">{{ t.label }}</span>
        <span class="km-tab__dot"></span>
      </router-link>
    </div>

    <!-- Shared gradient defs -->
    <svg width="0" height="0" aria-hidden="true" style="position:absolute;">
      <defs>
        <linearGradient id="kmg-home"   x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ff4d8d"/><stop offset="1" stop-color="#a020f0"/></linearGradient>
        <linearGradient id="kmg-video"  x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ff4d8d"/><stop offset="1" stop-color="#a020f0"/></linearGradient>
        <linearGradient id="kmg-chats"  x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ff4d8d"/><stop offset="1" stop-color="#a020f0"/></linearGradient>
        <linearGradient id="kmg-choose" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ff4d8d"/><stop offset="1" stop-color="#a020f0"/></linearGradient>
        <linearGradient id="kmg-spin"   x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ff4d8d"/><stop offset="1" stop-color="#a020f0"/></linearGradient>
        <linearGradient id="kmg-mine"   x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ff4d8d"/><stop offset="1" stop-color="#a020f0"/></linearGradient>
      </defs>
    </svg>
  </nav>
</template>

<script>
export default {
  data() {
    return {
      show: false,
      active: 0,
      scrolled: false,
      // 5 equal tabs
      allTabs: []
    };
  },
  computed: {
    tabLabels() {
      return [
        { index: 0, id:'home',    path:'/Home',    label: this.$t('nav.home') },
        { index: 1, id:'video',   path:'/Video',   label: this.$t('nav.cam') },
        { index: 2, id:'choose',  path:'/Choose',  label: this.$t('nav.choose') },
        { index: 3, id:'lottery', path:'/Lottery', label: this.$t('nav.certify') },
        { index: 4, id:'mine',    path:'/Mine',    label: this.$t('nav.profile') }
      ];
    }
  },
  watch: {
    $route(to) { this.sync(to); },
    '$i18n.locale'() { this.allTabs = this.tabLabels; },
  },
  mounted() {
    this.allTabs = this.tabLabels;
    this.sync(this.$route);
    this.$options._kmScroll = () => { this.scrolled = window.scrollY > 8; };
    window.addEventListener('scroll', this.$options._kmScroll, { passive: true });
  },
  beforeDestroy() {
    if(this.$options._kmScroll) window.removeEventListener('scroll', this.$options._kmScroll);
  },
  methods: {
    isActive(t){ return this.active === t.index; },
    sync(route){
      // Public routes + full-screen chat/support views hide the bottom bar
      // (they render their own full-height composer at the bottom edge).
      const HIDE = [
        'landing','login','register','login-legacy','register-legacy',
        'support','chat-thread'
      ];
      if(HIDE.indexOf(route.name) !== -1){ this.show = false; return; }
      this.show = true;
      // Map routes that map to a tab — everything else leaves all tabs inactive.
      // Active tab map. Discover gets its own index (99) so the center FAB lights up
      // only when user is on /Discover. /Choose map to "choose" tab.
      const map = {
        home: 0,
        video: 1,
        choose: 2, matches: 2,
        Lottery: 3,
        mine: 4,
        // All /Mine subpages keep the Profile tab lit
        Setting: 4, Infomation: 4, Setname: 4, Setsex: 4, Language: 4,
        Recharge: 4, SetPayPassword: 4, SetLoginPassword: 4, Notice: 4,
        Setbank: 4, BindCard: 4, Withdraw: 4, Personalreport: 4,
        WithdrawRecord: 4, GameRecord: 4, Details: 4,
        ServiceOnline: 4, ServicePage: 4, support: 4, Deposit: 4
      };
      this.active = Object.prototype.hasOwnProperty.call(map, route.name) ? map[route.name] : -1;
    }
  }
};
</script>

<style scoped>
/* ===== KissMora mobile tab bar ===== */
.km-tabbar {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 50;
  padding: 14px 12px calc(14px + env(safe-area-inset-bottom, 0));
  background: rgba(255,255,255,.9);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
  border-top: 1px solid rgba(26,16,51,.06);
  transition: box-shadow .25s ease, background .25s ease;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
}
.km-tabbar--scrolled {
  box-shadow: 0 -14px 36px rgba(26,16,51,.10);
  background: rgba(255,255,255,.95);
}
.km-tabbar__inner {
  max-width: 640px; margin: 0 auto;
  display: flex; align-items: stretch;
  gap: 0;
}
/* Every tab — including the Discover FAB — gets an equal 1/7 slot */
.km-tabbar__inner > .km-tab { flex: 1 1 0; min-width: 0; }

.km-tab {
  flex: 1;
  position: relative;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 7px;
  padding: 8px 4px;
  text-decoration: none; color: #8d7fa6;
  border-radius: 16px;
  transition: color .2s ease, background .2s ease, transform .15s ease;
  -webkit-tap-highlight-color: transparent;
}
.km-tab:active { transform: scale(.94); }
.km-tab__icon {
  width: 52px; height: 52px;
  display: flex; align-items: center; justify-content: center;
  transition: transform .25s cubic-bezier(.2,.9,.3,1.3);
}
.km-tab__icon svg { width: 100%; height: 100%; display: block; }
.km-tab__label {
  font-size: 13px; font-weight: 600; letter-spacing: .01em;
  line-height: 1;
  transition: color .2s, font-weight .2s;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  max-width: 100%;
}
.km-tab__dot {
  position: absolute; bottom: 2px; left: 50%; transform: translateX(-50%) scale(0);
  width: 5px; height: 5px; border-radius: 50%;
  background: linear-gradient(135deg, #ff4d8d, #a020f0);
  box-shadow: 0 0 8px rgba(255,77,141,.5);
  transition: transform .25s cubic-bezier(.2,.9,.3,1.3);
}

/* Active state */
.km-tab.active { color: #ff4d8d; }
.km-tab.active .km-tab__icon {
  transform: translateY(-3px) scale(1.1);
  filter: drop-shadow(0 6px 12px rgba(255,77,141,.4));
}
.km-tab.active .km-tab__label {
  font-weight: 700;
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.km-tab.active .km-tab__dot { transform: translateX(-50%) scale(1); }

/* ===== Classy "Choose" tab — slightly elevated & larger than peers ===== */
.km-tab--choose { color: #ff4d8d; }
.km-tab--choose .km-tab__icon {
  width: 54px; height: 54px; border-radius: 18px;
  padding: 10px;
  background: linear-gradient(135deg, #fff 0%, #ffe8f0 100%);
  border: 1.5px solid rgba(255,77,141,.25);
  box-shadow:
    0 8px 20px rgba(255,77,141,.18),
    inset 0 1px 0 rgba(255,255,255,.9);
  margin-top: -10px;
  transition: transform .2s ease, box-shadow .25s ease, border-color .2s ease;
}
.km-tab--choose:hover .km-tab__icon {
  transform: translateY(-2px);
  border-color: #ff4d8d;
  box-shadow: 0 14px 28px rgba(255,77,141,.32), inset 0 1px 0 rgba(255,255,255,.9);
}
.km-tab--choose.active .km-tab__icon {
  background: linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 14px 30px rgba(255,77,141,.45), 0 0 0 3px rgba(255,77,141,.2);
  transform: translateY(-2px);
  filter: none !important;
}
.km-tab--choose.active .km-tab__label {
  font-weight: 800;
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.km-tab--choose .km-tab__dot { bottom: 2px; }

/* ===== Responsive ===== */
@media (min-width: 520px) {
  .km-tab__icon { width: 56px; height: 56px; }
  .km-tab__label { font-size: 14px; }
  .km-tab--choose .km-tab__icon {
    width: 64px; height: 64px; border-radius: 20px; padding: 12px; margin-top: -12px;
  }
}
@media (max-width: 420px) {
  .km-tabbar { padding: 12px 8px calc(12px + env(safe-area-inset-bottom, 0)); }
  .km-tab__icon { width: 44px; height: 44px; }
  .km-tab__label { font-size: 11.5px; }
  .km-tab--choose .km-tab__icon {
    width: 50px; height: 50px; padding: 9px; margin-top: -8px;
  }
}
@media (max-width: 360px) {
  .km-tabbar { padding: 10px 6px calc(10px + env(safe-area-inset-bottom, 0)); }
  .km-tab__icon { width: 38px; height: 38px; }
  .km-tab__label { font-size: 10.5px; }
  .km-tab--choose .km-tab__icon {
    width: 44px; height: 44px; padding: 8px; margin-top: -6px; border-radius: 14px;
  }
}

/* Desktop: hide the bottom bar — DesktopNav at the top handles navigation */
@media (min-width: 960px) {
  .km-tabbar { display: none !important; }
}
</style>
